import { useEffect, useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze, zalozkyModulu } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import { REZERVA_LISTY } from '../components/MobilniLista';
import { SKLO_SVETLE } from '../lib/uiTokens';
import ObjednavkySeznam from '../components/objednavky/ObjednavkySeznam';
import RezervaceModul from '../components/rezervace/RezervaceModul';
import DnesPrijdou from '../components/rezervace/DnesPrijdou';
import PanelProvoznihoDne from '../components/objednavky/PanelProvoznihoDne';
import Reklamace from '../components/objednavky/Reklamace';
import { SECTION_COLOR, SECTION_BG } from '../components/objednavky/ui';
import NastaveniModulu, { NastaveniTlacitko } from '../components/layout/NastaveniModulu';
import NastaveniHodnoty from '../components/layout/NastaveniHodnoty';

// ⚙ Objednávky → Rezervace (dřív jen .env / config/pos.php)
const POLE_REZERVACI = [
    { klic: 'rezervace.vychozi_delka_min', nazev: 'Výchozí délka rezervace (min)', typ: 'number', vychozi: 120, popis: 'Na jak dlouho se stůl blokuje, když obsluha délku nezmění.' },
    { klic: 'rezervace.predstih_minut', nazev: 'Předstih na pokladně (min)', typ: 'number', vychozi: 60, popis: 'Kolik minut předem pokladna obarví stůl jako rezervovaný.' },
    { klic: 'rezervace.krok_minut', nazev: 'Krok času (min)', typ: 'number', vychozi: 15, popis: 'Rastr kalendáře a zadávaného času.' },
    { klic: 'rezervace.provoz_od', nazev: 'Kalendář od', typ: 'time', vychozi: '10:00', popis: 'Začátek časové osy v kalendáři rezervací.' },
    { klic: 'rezervace.provoz_do', nazev: 'Kalendář do', typ: 'time', vychozi: '22:00', popis: 'Konec časové osy v kalendáři rezervací.' },
];

// ⚙ Objednávky → Slíbené časy (SlibNastaveni)
const POLE_SLIBU = [
    { klic: 'slib.automaticky', nazev: 'Navyšovat podle vytížení', typ: 'prepinac', vychozi: 0, popis: 'Zapnuto: k základu se přičte fronta kuchyně a čekání na řidiče. Vypnuto: slibuje se základ + jen to, co obsluha ručně přidá na pokladně (platí pro všechny pokladny).' },
    { klic: 'slib.bistro_min', nazev: 'Na místě (min)', typ: 'number', vychozi: 15, popis: 'Výchozí slíbený čas pro konzumaci na místě — od odeslání do kuchyně.' },
    { klic: 'slib.vydej_min', nazev: 'S sebou (min)', typ: 'number', vychozi: 15, popis: 'Výchozí slíbený čas pro objednávku s sebou.' },
    { klic: 'slib.bar_min', nazev: 'Jen bar (min)', typ: 'number', vychozi: 5, popis: 'Objednávka, která jde celá na bar (nápoje). Vytížení kuchyně se nepřičítá.' },
    { klic: 'slib.rozvoz_min', nazev: 'Rozvoz bez zóny (min)', typ: 'number', vychozi: 45, popis: 'Rozvoz jinak bere minimum ze své zóny (Rozvoz → Zóny → Slib doby dovozu). Tohle platí jen mimo zóny.' },
    { klic: 'slib.priprava_min', nazev: 'Doba přípravy (min)', typ: 'number', vychozi: 12, popis: 'Semafor na kartě: oranžová, když zbývá méně než doba přípravy; červená pod polovinu.' },
    { klic: 'slib.kuchyne_vykon_kusu', nazev: 'Výkon kuchyně (kusů jídla za 10 min na člověka)', typ: 'number', vychozi: 5, popis: 'Fronta kuchyně = nedodělané kusy ÷ (lidé ve směně × výkon). Lidé = přihlášení v docházce s kariérou Obsluha nebo Pomocná obsluha.' },
    { klic: 'slib.kuchyne_lidi_vychozi', nazev: 'Lidí v kuchyni, když nikdo není v docházce', typ: 'number', vychozi: 2 },
    { klic: 'slib.kuchyne_tolerance_min', nazev: 'Fronta kuchyně bez navýšení (min)', typ: 'number', vychozi: 5, popis: 'Kratší frontu kuchyně pobere — slib se navyšuje až o to, co ji přesáhne.' },
    { klic: 'slib.rozvoz_kolo_min', nazev: 'Jedno kolo řidiče (min)', typ: 'number', vychozi: 30, popis: 'Rozvoz počítá, za jak dlouho bude mít objednávka řidiče (řidiči ve směně + odhad návratu z GPS). Tohle se použije, když návrat odhadnout nejde.' },
    { klic: 'slib.rozvoz_kapacita', nazev: 'Bez řidiče v docházce: rozvozů na odvoz bez navýšení', typ: 'number', vychozi: 3, popis: 'Náhradní pravidlo, když není přihlášený žádný řidič.' },
    { klic: 'slib.rozvoz_za_kus_min', nazev: 'Bez řidiče v docházce: navýšení za rozvoz nad kapacitu (min)', typ: 'number', vychozi: 5 },
];

/**
 * Modul Objednávky (sekce Obchod, id `objednavky` — id je kód oprávnění, nemění se).
 *
 * 2026-10-06 až 10-12 se jmenoval Hosté a měl i záložku Zákazníci; ta je od
 * 2026-10-12 samostatný modul `zakaznici` (pages/Zakaznici.jsx). Tady zůstává
 * provoz dne: objednávky a rezervace.
 *
 * Záložky (klíče přístupů):
 *  - Objednávky (`objednavky.zive`) — výběr období: Dnes živě, cokoli jiného
 *    je historie. Delší období a export odemyká `objednavky.historie`
 *    (přístup bez vlastní záložky). Do 2026-10-06 záložky Živě a Historie.
 *    U Dnes v panelu „Dnes přijdou" — dnešní rezervace.
 *  - Rezervace (`objednavky.rezervace`) — dřív samostatný modul `rezervace`
 *  - Reklamace (`objednavky.vyrizeni`, 2026-10-25) — něco chybí, stížnost, pochvala
 *    a jejich dořešení (částka, vrácení / body provedené v Objedname)
 *
 * Proklik z jiného modulu: `navigateTo('objednavky', { tab, id, datum? })`.
 * Staré adresy (`rezervace`, `tab: 'historie'`) přesměruje `app.jsx`, resp.
 * tahle stránka; `tab: 'zakaznici'` vede do modulu Zákazníci (`app.jsx`).
 */
export default function Objednavky({ user, initialTab = null, navParams = null, onNavigate }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile] = useState(() => window.innerWidth < 768);

    const povolene = zalozkyModulu('objednavky', user).map(z => z.id);
    // Stará záložka Historie je teď období v záložce Objednávky
    const naZalozku = (t) => (t === 'historie' ? 'zive' : t);
    const [tab, setTab] = useState(() => {
        const t = naZalozku(initialTab);
        return t && povolene.includes(t) ? t : povolene[0] ?? 'zive';
    });
    // Co otevřít v záložce (proklik) — platí jen pro záložku, kam míří
    const [otevrit, setOtevrit] = useState(() => (navParams?.id ? { tab: naZalozku(navParams.tab) || tab, ...navParams } : null));
    useEffect(() => {
        const t = naZalozku(initialTab);
        if (t && povolene.includes(t)) setTab(t);
        setOtevrit(navParams?.id || navParams?.datum ? { ...navParams, tab: t || tab } : null);
    }, [initialTab, navParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const prepnout = (t) => { setTab(t); setOtevrit(null); };

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const inputStyle = {
        width: '100%', height: 32, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    // Na mobilu připnuté k výřezu (norma §1b), na desktopu na výšku obsahu
    const pripnuta = isMobile
        ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
        : { height: '100%', minHeight: 0 };

    const muzeZakaznici = muze(user, 'zakaznici.seznam');
    const muzeRezervace = muze(user, 'objednavky.rezervace');
    // Přehled provozního dne (kdo odpovídal, storna, zásahy) — vedoucí s Historií
    const muzeProvoz = muze(user, 'objednavky.historie');
    const [nastaveni, setNastaveni] = useState(false);
    const jdi = (t, params) => (onNavigate ? onNavigate('objednavky', { tab: t, ...params }) : (setTab(t), setOtevrit({ tab: t, ...params })));
    // Karta hosta je v modulu Zákazníci
    const naZakaznika = muzeZakaznici && onNavigate ? (id) => onNavigate('zakaznici', { tab: 'seznam', id }) : null;
    const naObjednavku = povolene.includes('zive') ? (id) => jdi('zive', { id }) : null;
    const naRezervaci = muzeRezervace ? (r) => jdi('rezervace', { id: r.id, datum: r.datum }) : null;
    const otevritId = otevrit?.tab === tab ? otevrit.id ?? null : null;

    const spolecne = { isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole: isMobile ? REZERVA_LISTY : null };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                {!isMobile && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                            <ShoppingBag size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Objednávky</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Objednávky a rezervace — rozvoz, bistro i výdej</p>
                        </div>
                        {muze(user, 'objednavky.rezervace', 'upravuje') && (
                            <NastaveniTlacitko onClick={() => setNastaveni(true)} barva={SECTION_COLOR} isDark={isDark} popis="Nastavení objednávek" />
                        )}
                    </div>
                )}
                <TabBar modul="objednavky" user={user} aktivni={tab} onZmena={prepnout} barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
                {nastaveni && (
                    <NastaveniModulu nadpis="Nastavení objednávek" barva={SECTION_COLOR} isDark={isDark} onClose={() => setNastaveni(false)}
                        sekce={[
                            { id: 'slib', nazev: 'Slíbené časy', popis: 'výchozí časy, doba přípravy, vytížení', obsah: () => <NastaveniHodnoty pole={POLE_SLIBU} isDark={isDark} /> },
                            { id: 'rezervace', nazev: 'Rezervace', popis: 'délka, předstih, kalendář', obsah: () => <NastaveniHodnoty pole={POLE_REZERVACI} isDark={isDark} /> },
                        ]} />
                )}
            </div>

            <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column' }}>
                <div className="flex flex-col" style={{ ...pripnuta, flex: isMobile ? undefined : 1 }}>
                    {tab === 'zive' && (
                        <ObjednavkySeznam {...spolecne} otevritId={otevritId} onZakaznik={naZakaznika}
                            muzeHistorie={muze(user, 'objednavky.historie')}
                            vychoziObdobi={otevrit?.tab === 'zive' && navParams?.tab === 'historie' ? '7' : 'dnes'}
                            panelNavic={muzeRezervace || muzeProvoz ? ({ zive, kompaktni, den }) => (
                                <>
                                    {muzeRezervace && zive && (
                                        <DnesPrijdou isDark={isDark} mutedColor={mutedColor} kompaktni={kompaktni} onOtevrit={naRezervaci} />
                                    )}
                                    {muzeProvoz && den && (
                                        <PanelProvoznihoDne den={den} muted={mutedColor} isDark={isDark} kompaktni={kompaktni} />
                                    )}
                                </>
                            ) : null} />
                    )}
                    {tab === 'vyrizeni' && (
                        <Reklamace {...spolecne} muzeUpravit={muze(user, 'objednavky.vyrizeni', 'upravuje')}
                            onObjednavka={naObjednavku} onZakaznik={naZakaznika} />
                    )}
                    {tab === 'rezervace' && (
                        <RezervaceModul {...spolecne} otevritId={otevritId} otevritDatum={otevrit?.tab === 'rezervace' ? otevrit.datum ?? null : null}
                            muzeZakaznici={muzeZakaznici} onZakaznik={naZakaznika} onObjednavka={naObjednavku} />
                    )}
                </div>
            </div>
        </div>
    );
}
